import React, { useEffect, useMemo, useState } from "react";
import { Button, Select, Input, Table, Tag, Card, Row, Col, Statistic, Alert, Avatar, Space, message } from "antd";
import styled from "styled-components";
import {
  BRAND, STATUS_LABELS, STATUS_COLORS, fmtInt, fmtDec, fmtUsd, fmtPeriod, teacherName, statusSummary, rateLabel,
  loadSelectedDays, saveSelectedDays, selectionSummary, downloadCSV, apiError, ACTIVE_OPTIONS, matchesActive,
} from "./utils";
import DaySelectModal from "./DaySelectModal";
import ManagerModal from "./ManagerModal";
import FxModal from "./FxModal";
import TeacherDrawer from "./TeacherDrawer";
import PerformanceView from "./PerformanceView";

const axios = require("axios").default;

const PageWrap = styled.div`
  padding: 24px;
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const Eyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 1.5px;
  color: ${BRAND};
  font-weight: 600;
`;

const Title = styled.h1`
  margin: 2px 0 0;
  font-size: 24px;
  span {
    color: #999;
    font-weight: 400;
    font-size: 18px;
  }
`;

const UNASSIGNED = "unassigned";

/** page: "payroll" (Maaş Yönetimi) veya "performance" (Performans & Öğrenci Yorumları); menüden ayrı sayfalar */
const Payroll = ({ page = "payroll" }) => {
  const [days, setDays] = useState(loadSelectedDays);
  const [data, setData] = useState({ teachers: [], managers: [], fx: {}, periods: [] });
  const [loading, setLoading] = useState(false);
  const [managerFilter, setManagerFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [dayModal, setDayModal] = useState(false);
  const [managerModal, setManagerModal] = useState(false);
  const [fxModal, setFxModal] = useState(false);
  const [activeTeacher, setActiveTeacher] = useState(null);

  const load = async (selected = days) => {
    setLoading(true);
    try {
      const { data: res } = await axios.post("payroll/overview", { dates: selected });
      setData(res);
    } catch (err) {
      message.error(apiError(err, "Maaş verileri alınamadı."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyDays = (selected) => {
    setDays(selected);
    saveSelectedDays(selected);
    setDayModal(false);
    load(selected);
  };

  const managerNames = useMemo(() => Object.fromEntries(data.managers.map((m) => [m.id, m.name])), [data.managers]);

  // Manager filtresi grand total, liste ve performans için ortak
  const byManager = useMemo(
    () =>
      data.teachers.filter((t) =>
        managerFilter === "all" ? true : managerFilter === UNASSIGNED ? !t.manager_id : t.manager_id === managerFilter
      ),
    [data.teachers, managerFilter]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    const e = emailSearch.trim().toLocaleLowerCase("tr-TR");
    return byManager.filter(
      (t) =>
        (!q || teacherName(t).toLocaleLowerCase("tr-TR").includes(q)) &&
        (!e || (t.email || "").toLocaleLowerCase("tr-TR").includes(e)) &&
        (statusFilter === "all" || statusSummary(t.monthly) === statusFilter) &&
        matchesActive(t, activeFilter)
    );
  }, [byManager, search, emailSearch, statusFilter, activeFilter]);

  const total = (k) => byManager.reduce((s, t) => s + t.totals[k], 0);
  const scored = byManager.filter((t) => t.perf.bookings > 0 || t.perf.reviewCount > 0);
  const avgScore = scored.length ? scored.reduce((s, t) => s + t.perf.score, 0) / scored.length : 0;
  const missingFx = data.periods.filter((p) => !data.fx[p]);

  const exportCSV = () => {
    downloadCSV(
      `qarint-maas-${days[0]}_${days[days.length - 1]}.csv`,
      ["Eğitmen", "Email", "Manager", "Ülke", "Ücret PHP/sa (başarılı / boş)", "Açılan dk", "Rezervasyon dk", "Başarılı dk", "Deneme dk",
        "Boş dk", "Kaçırılan adet", "Doluluk %", "Brüt PHP", "Ceza PHP", "Net PHP", "Net USD", "Durum"],
      rows.map((t) => [
        teacherName(t), t.email, managerNames[t.manager_id] || "Atanmamış", t.country, rateLabel(t.rates), t.totals.opened,
        t.totals.booked, t.totals.completed, t.totals.trial, t.totals.idle, t.missedCount, fmtDec(t.totals.utilization),
        fmtDec(t.totals.gross), fmtDec(t.totals.penalty), fmtDec(t.totals.net),
        t.totals.fxMissing ? "" : fmtDec(t.totals.usd), STATUS_LABELS[statusSummary(t.monthly)],
      ])
    );
  };

  const columns = [
    {
      title: "Eğitmen",
      key: "name",
      sorter: (a, b) => teacherName(a).localeCompare(teacherName(b)),
      render: (_, t) => (
        <Space>
          <Avatar size="small" src={t.image || undefined} style={{ background: BRAND }}>{t.first_name?.[0]}</Avatar>
          <span>{teacherName(t)}</span>
          {Number(t.user_status) !== 1 && <Tag>Pasif</Tag>}
        </Space>
      ),
    },
    { title: "Manager", key: "manager", render: (_, t) => managerNames[t.manager_id] || <span style={{ color: "#bbb" }}>Atanmamış</span> },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Açılan (dk)", key: "opened", align: "right", sorter: (a, b) => a.totals.opened - b.totals.opened, render: (_, t) => fmtInt(t.totals.opened) },
    { title: "Rezervasyon (dk)", key: "booked", align: "right", render: (_, t) => fmtInt(t.totals.booked) },
    { title: "Başarılı (dk)", key: "completed", align: "right", render: (_, t) => fmtInt(t.totals.completed) },
    { title: "Deneme (dk)", key: "trial", align: "right", render: (_, t) => fmtInt(t.totals.trial) },
    { title: "Öğretmen Kaçırdı (adet)", key: "missed", align: "right", render: (_, t) => t.missedCount },
    { title: "Silinen (adet)", key: "deleted", align: "right", render: (_, t) => t.deletedCount },
    { title: "Doluluk", key: "util", align: "right", sorter: (a, b) => a.totals.utilization - b.totals.utilization, render: (_, t) => `%${fmtDec(t.totals.utilization)}` },
    {
      title: "Net USD",
      key: "usd",
      align: "right",
      sorter: (a, b) => a.totals.usd - b.totals.usd,
      render: (_, t) => (
        <b>
          {fmtUsd(t.totals.usd)}
          {t.totals.fxMissing && <Tag color="red" style={{ marginLeft: 6 }}>Kur eksik</Tag>}
        </b>
      ),
    },
    {
      title: "Durum",
      key: "status",
      render: (_, t) => {
        const s = statusSummary(t.monthly);
        return <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag>;
      },
    },
    { title: "İşlem", key: "act", render: (_, t) => <Button size="small" onClick={() => setActiveTeacher(t.id)}>Detay</Button> },
  ];

  const payrollTab = (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card><Statistic title="Toplam Eğitmen" value={byManager.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Açılan Toplam (dk)" value={fmtInt(total("opened"))} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Rezervasyon Toplamı (dk)" value={fmtInt(total("booked"))} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Net Maaş Toplamı (USD)" value={fmtUsd(total("usd"))} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Başarılı Ders (dk)" value={fmtInt(total("completed"))} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Deneme Dersi (dk)" value={fmtInt(total("trial"))} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Öğretmenin Kaçırdığı (onaylı)" value={byManager.reduce((s, t) => s + t.missedCount, 0)} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Ortalama Skor" value={fmtDec(avgScore)} suffix="/ 100" /></Card></Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
          <span>
            <b>💱 Dönem Döviz Kuru</b>
            <span style={{ color: "#666" }}> · Maaşlar PHP olarak hesaplanır; USD karşılığı ayın kuru ile gösterilir. Onaylanan maaşların kuru sabitlenir.</span>
          </span>
          <Space wrap>
            {data.periods.map((p) => (
              <Tag key={p} color={data.fx[p] ? "default" : "red"} style={{ textTransform: "capitalize" }}>
                {fmtPeriod(p)}: {data.fx[p] ? `1 USD = ${fmtDec(data.fx[p])} PHP` : "kur girilmedi"}
              </Tag>
            ))}
            <Button size="small" onClick={() => setFxModal(true)}>Düzenle</Button>
          </Space>
        </Space>
      </Card>

      {missingFx.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${missingFx.map(fmtPeriod).join(", ")} için döviz kuru girilmemiş; bu aylara ait USD tutarları hesaplanmaz ve bordro onaylanamaz.`}
        />
      )}

      <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
        <b>Hesaplama:</b> (Başarılı ders dk + Deneme dk) × başarılı ders saatlik ücreti / 60 + Boş süre dk × boş saatlik ücret / 60 − Onaylı cezalar = Net PHP. Ücretler eğitmen detayında gün bazlı elle girilir; girilmeyen günlerde 180 / 40 PHP kullanılır.
        Boş süre, açılan dakikadan slot içindeki başarılı ders/deneme dakikaları çıkarılarak bulunur. Rezervasyon yapılıp
        gerçekleşmeyen süre başarılı ders sayılmaz. Süreler sistemdeki günlük ders kayıtlarından gelir; yalnızca bitmiş slotlar sayılır.
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <Space wrap>
          <Input placeholder="Ad / Soyad" allowClear value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
          <Input placeholder="Email" allowClear value={emailSearch} onChange={(e) => setEmailSearch(e.target.value)} style={{ width: 200 }} />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }} options={[
            { value: "all", label: "Durum (tümü)" },
            { value: "draft", label: "Taslak" },
            { value: "approved", label: "Onaylandı" },
            { value: "paid", label: "Ödendi" },
            { value: "mixed", label: "Karma" },
          ]} />
          <Select value={activeFilter} onChange={setActiveFilter} style={{ width: 180 }} options={ACTIVE_OPTIONS} />
          <span style={{ color: "#888" }}>{rows.length} eğitmen</span>
        </Space>
        <Button onClick={exportCSV} disabled={!rows.length}>⇩ CSV Dışa Aktar</Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: true }}
        size="middle"
        onRow={(t) => ({ onDoubleClick: () => setActiveTeacher(t.id) })}
        summary={() =>
          rows.length > 0 && (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}><b>GRAND TOTAL</b> <span style={{ color: "#888" }}>({rows.length} eğitmen, tüm sayfalar)</span></Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">{fmtInt(rows.reduce((s, t) => s + t.totals.opened, 0))}</Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">{fmtInt(rows.reduce((s, t) => s + t.totals.booked, 0))}</Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">{fmtInt(rows.reduce((s, t) => s + t.totals.completed, 0))}</Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">{fmtInt(rows.reduce((s, t) => s + t.totals.trial, 0))}</Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">{rows.reduce((s, t) => s + t.missedCount, 0)}</Table.Summary.Cell>
              <Table.Summary.Cell index={8} align="right">{rows.reduce((s, t) => s + t.deletedCount, 0)}</Table.Summary.Cell>
              <Table.Summary.Cell index={9} />
              <Table.Summary.Cell index={10} align="right"><b>{fmtUsd(rows.reduce((s, t) => s + t.totals.usd, 0))}</b></Table.Summary.Cell>
              <Table.Summary.Cell index={11} colSpan={2} />
            </Table.Summary.Row>
          )
        }
      />
    </>
  );

  return (
    <PageWrap>
      <Top>
        <div>
          <Eyebrow>TRAINER MANAGEMENT / PAYROLL</Eyebrow>
          <Title>
            {page === "payroll" ? "TRAINER PAYROLL" : "TRAINER PERFORMANCE"}{" "}
            <span>/ {page === "payroll" ? "Öğretmen Maaşları" : "Öğretmen Değerlendirme"}</span>
          </Title>
          <div style={{ color: "#888", marginTop: 4 }}>{selectionSummary(days)}</div>
        </div>
        <Space wrap>
          <Button type="primary" onClick={() => setDayModal(true)}>▦ Tarihleri seç ({days.length} gün)</Button>
          <Select
            value={managerFilter}
            onChange={setManagerFilter}
            style={{ width: 200 }}
            options={[
              { value: "all", label: "Tüm Managerlar" },
              { value: UNASSIGNED, label: "Atanmamış" },
              ...data.managers.map((m) => ({ value: m.id, label: m.name })),
            ]}
          />
          <Button onClick={() => setManagerModal(true)}>＋ Manager Yönetimi</Button>
          <Button onClick={() => setFxModal(true)} disabled={!data.periods.length}>⚙ Döviz Kuru</Button>
        </Space>
      </Top>

      <Alert
        type="info"
        style={{ marginBottom: 16 }}
        message={
          <>
            <b>Veri kaynağı:</b> Çalışma süreleri, iptaller ve öğrenci yorumları Qarint sisteminden; kaçırılan ders adedi ve kesinti
            tutarı maaş ekranından manuel girilir.
          </>
        }
      />

      {page === "payroll" ? (
        payrollTab
      ) : (
        <PerformanceView teachers={byManager} managerNames={managerNames} days={days} loading={loading} />
      )}

      <DaySelectModal open={dayModal} value={days} onCancel={() => setDayModal(false)} onApply={applyDays} />
      <ManagerModal open={managerModal} onClose={() => setManagerModal(false)} onChanged={() => load()} />
      <FxModal
        open={fxModal}
        periods={data.periods}
        fx={data.fx}
        onClose={() => setFxModal(false)}
        onSaved={() => {
          setFxModal(false);
          load();
        }}
      />
      <TeacherDrawer
        teacherId={activeTeacher}
        days={days}
        managers={data.managers}
        onClose={() => setActiveTeacher(null)}
        onChanged={() => load()}
      />
    </PageWrap>
  );
};

export default Payroll;
